import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useAuth } from "./AuthContext";
import { api } from "../lib/api";
import { toast } from "sonner";
import { useSpotifySDK } from "../lib/useSpotifySDK";
import { getDominantColor, accentStyleVars } from "../lib/albumColor";

/**
 * Shared music state. Keeps a single Spotify Web Playback SDK instance alive
 * for the duration of the in-app session and exposes a unified `play(item)`
 * API that the rest of the UI can call regardless of whether the item is a
 * track, playlist, or album.
 *
 * Non-Premium users can still SEE the music browsing UI; the Player component
 * gates the playback controls with an upgrade message based on `sdkError`.
 */
const MusicContext = createContext(null);

const parseSpotifyEmbed = (url) => {
  if (!url) return null;
  const m = url.match(/spotify\.com\/(?:intl-[a-z]+\/)?(playlist|album|track|episode|show)\/([A-Za-z0-9]+)/);
  if (!m) return null;
  return { kind: m[1], id: m[2] };
};

export function MusicProvider({ children }) {
  const { user } = useAuth();
  const [current, setCurrent] = useState(() => parseSpotifyEmbed(user?.spotify_playlist_url));
  const [pillOpen, setPillOpen] = useState(false);
  const [status, setStatus] = useState(null);
  const [accentRgb, setAccentRgb] = useState(null);

  const loadStatus = useCallback(async () => {
    if (!user) {
      setStatus(null);
      return;
    }
    try {
      const { data } = await api.get("/spotify/status");
      setStatus(data);
    } catch {
      setStatus({ configured: false, connected: false });
    }
  }, [user]);

  useEffect(() => {
    loadStatus();
    const onMsg = (e) => {
      if (e.data?.type === "spotify-connected") loadStatus();
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, [loadStatus]);

  // Bootstrap the SDK ONLY for connected Premium users. Free accounts get the
  // browsing UI + a clear upgrade prompt instead of a broken player.
  const sdkEnabled = !!status?.connected && status?.premium === true;
  const {
    ready: sdkReady,
    deviceId,
    state: sdkState,
    error: sdkError,
    controls: sdk,
  } = useSpotifySDK({ enabled: sdkEnabled });

  // Dynamic album-art accent. Driven by `user.album_accent_enabled` so users
  // can opt out from Profile. Re-runs whenever the current track image changes.
  // The colour is kept in React state and applied as inline CSS variables on
  // the player wrappers only — so the accent never leaks to the rest of the
  // app (no `:root` mutation here).
  const accentEnabled = user?.album_accent_enabled !== false; // default ON
  useEffect(() => {
    if (!accentEnabled) {
      setAccentRgb(null);
      return;
    }
    const img = sdkState?.track_window?.current_track?.album?.images?.[0]?.url;
    if (!img) {
      setAccentRgb(null);
      return;
    }
    let cancelled = false;
    getDominantColor(img).then((rgb) => {
      if (!cancelled) setAccentRgb(rgb);
    });
    return () => {
      cancelled = true;
    };
  }, [accentEnabled, sdkState?.track_window?.current_track?.album?.images]);

  const accentStyle = accentStyleVars(accentRgb);

  // Reset current if the saved profile URL changes and we haven't picked anything yet.
  useEffect(() => {
    const fromProfile = parseSpotifyEmbed(user?.spotify_playlist_url);
    if (fromProfile && !current) setCurrent(fromProfile);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.spotify_playlist_url]);

  /**
   * Play any Spotify entity. `item` shape:
   *   { id, uri, name } — uri may be spotify:track:... | spotify:playlist:... | spotify:album:...
   * `kind` is informational (used to drive the embed iframe id legacy state).
   */
  const playItem = useCallback(
    async (item, kind, { silent = false, autoOpenPill = true } = {}) => {
      if (!item?.id || !kind) return;
      setCurrent({ kind, id: item.id });
      if (autoOpenPill) setPillOpen(true);

      if (!status?.connected) {
        if (!silent) toast.message("Connect Spotify to play music in-app.");
        return;
      }
      if (!status?.premium) {
        if (!silent)
          toast.message("Spotify Premium is required for in-app playback.", {
            description: "Free accounts can still preview via Spotify's iframe.",
          });
        return;
      }
      if (!sdkReady || !deviceId) {
        if (!silent) toast.message("Spinning up the player…", { duration: 2000 });
        // Defer; user can re-click in a moment.
        return;
      }
      try {
        await sdk.activateElement();
        await sdk.play({ uri: item.uri });
        if (!silent) toast.success(`Playing ${item.name || ""}`.trim());
      } catch (err) {
        toast.error(err.response?.data?.detail || "Playback failed");
      }
    },
    [status?.connected, status?.premium, sdkReady, deviceId, sdk]
  );

  const sendControl = useCallback(
    async (action) => {
      if (!sdkReady) {
        toast.message("Spotify is starting up…");
        return;
      }
      try {
        await sdk.activateElement();
        if (action === "pause") await sdk.pause();
        else if (action === "play") await sdk.resume();
        else if (action === "toggle") await sdk.togglePlay();
        else if (action === "next") await sdk.next();
        else if (action === "previous") await sdk.prev();
      } catch (err) {
        toast.error(err.response?.data?.detail || `Could not ${action}`);
      }
    },
    [sdk, sdkReady]
  );

  const connect = useCallback(async () => {
    try {
      const { data } = await api.get("/spotify/login");
      const w = window.open(data.auth_url, "spotify-oauth", "width=520,height=720");
      if (!w) window.location.href = data.auth_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Connect failed");
    }
  }, []);

  return (
    <MusicContext.Provider
      value={{
        current,
        setCurrent,
        playItem,
        sendControl,
        connect,
        pillOpen,
        setPillOpen,
        status,
        refreshStatus: loadStatus,
        // SDK surface
        sdkReady,
        sdkError,
        sdkState,
        sdkDeviceId: deviceId,
        sdk,
        // Album-art accent (scoped to the music player; spread onto its root)
        accentStyle,
      }}
    >
      {children}
    </MusicContext.Provider>
  );
}

export const useMusic = () => {
  const ctx = useContext(MusicContext);
  if (!ctx) throw new Error("useMusic must be used inside <MusicProvider>");
  return ctx;
};

export { parseSpotifyEmbed };
